import {
    ComponentFactoryResolver,
    Directive,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    TemplateRef,
    ViewContainerRef
} from '@angular/core';
import { getCaretPosition, getValue, insertValue, setCaretPosition } from 'src/app/shared/utilities/mentions-utils';
import { MentionConfig } from 'src/app/shared/models/mention-config';
import { MentionListComponent } from 'src/app/shared/components/mention-list/mention-list.component';
import { BACKSPACE, DOWN_ARROW, ENTER, ESCAPE, SHIFT, SPACE, TAB, UP_ARROW } from '@angular/cdk/keycodes';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

const KEY_BUFFERED = 229;
/**
 * Angular Mentions. Original code on
 * https://github.com/dmacfarlane/angular-mentions
 *
 * Copyright (c) 2017 Dan MacFarlane
 * Updated by Bernini 2024
 */
@UntilDestroy()
@Directive({
    selector: '[novaMention], [novaMentionConfig]',
    host: {
        '(keydown)': 'keyHandler($event)',
        '(input)': 'inputHandler($event)',
        '(mouseup)': 'mouseDownHandler($event)',
        '(blur)': 'blurHandler($event)',
        autocomplete: 'off'
    },
    standalone: true
})
export class MentionDirective {
    private _element;
    private _componentResolver;
    private _viewContainerRef;
    private mentionItems;
    @Input()
    mentionConfig: MentionConfig;
    private activeConfig;
    private DEFAULT_CONFIG;
    @Input()
    mentionListTemplate: TemplateRef<any>;

    @Output()
    searchTerm: EventEmitter<string>;

    @Output()
    itemSelected: EventEmitter<any>;

    @Output()
    opened: EventEmitter<any>;

    @Output()
    closed: EventEmitter<any>;
    private triggerChars;
    private searchString;
    private startPos;
    private startNode;
    private searchList;
    private searching;
    private iframe;
    private lastKeyCode;

    constructor(
        _element: ElementRef,
        _componentResolver: ComponentFactoryResolver,
        _viewContainerRef: ViewContainerRef
    ) {
        this._element = _element;
        this._componentResolver = _componentResolver;
        this._viewContainerRef = _viewContainerRef;
        // the provided configuration object
        this.mentionConfig = { items: [] };
        this.DEFAULT_CONFIG = {
            items: [],
            triggerChar: '@',
            labelKey: 'label',
            maxItems: -1,
            allowSpace: true,
            returnTrigger: false,
            mentionSelect: (item, triggerChar) => {
                return this.activeConfig.triggerChar + item[this.activeConfig.labelKey];
            },
            mentionFilter: (searchString, items) => {
                const searchStringLowerCase = searchString.toLowerCase();
                return items.filter((e) =>
                    e[this.activeConfig.labelKey].toLowerCase().startsWith(searchStringLowerCase)
                );
            }
        };
        // event emitted whenever the search term changes
        this.searchTerm = new EventEmitter();
        // event emitted when an item is selected
        this.itemSelected = new EventEmitter();
        // event emitted whenever the mention list is opened or closed
        this.opened = new EventEmitter();
        this.closed = new EventEmitter();
        this.triggerChars = {};
    }

    @Input('novaMention')
    set novaMention(items) {
        this.mentionItems = items;
        this.updateConfig();
    }

    updateConfig() {
        const config = this.mentionConfig;
        this.triggerChars = {};
        // use items from directive if they have been set
        if (this.mentionItems) {
            config.items = this.mentionItems;
        }
        this.addConfig(config);
        // nested configs
        if (config.mentions) {
            config.mentions.forEach((config) => this.addConfig(config));
        }
    }

    // add configuration for a trigger char
    addConfig(config) {
        // defaults
        const defaults = Object.assign({}, this.DEFAULT_CONFIG);
        config = Object.assign(defaults, config);
        // items
        let items = config.items;
        if (items && items.length > 0) {
            // convert strings to objects
            if (typeof items[0] === 'string') {
                items = items.map((label) => {
                    const object = {};
                    object[config.labelKey] = label;
                    return object;
                });
            }
            if (config.labelKey) {
                // remove items without a labelKey (as it's required to filter the list)
                items = items.filter((e) => e[config.labelKey]);
                if (!config.disableSort) {
                    items.sort((a, b) => a[config.labelKey].localeCompare(b[config.labelKey]));
                }
            }
        }
        config.items = items;
        // add the config
        this.triggerChars[config.triggerChar] = config;
        // for async update while menu/search is active
        if (this.activeConfig && this.activeConfig.triggerChar === config.triggerChar) {
            this.activeConfig = config;
            this.updateSearchList();
        }
    }

    setIframe(iframe: HTMLIFrameElement) {
        this.iframe = iframe;
    }

    stopEvent(event) {
        if (!event.wasClick) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
        }
    }

    blurHandler(event) {
        this.stopEvent(event);
        const target = (event as FocusEvent).relatedTarget as Element;
        //the user clicked something but the scrollbar
        if (target === null || target?.localName !== 'mat-sidenav') {
            this.stopSearch();
        }
    }

    inputHandler(event, nativeElement = this._element.nativeElement) {
        if (event.data === '@') {
            const keyCode = event.data.charCodeAt(0);
            this.keyHandler({ keyCode, inputEvent: true }, nativeElement);
        }
    }

    keyHandler(event, nativeElement = this._element.nativeElement) {
        this.lastKeyCode = event.keyCode;
        if (event.isComposing || event.keyCode === KEY_BUFFERED) {
            return;
        }
        const val = getValue(nativeElement);
        let pos = getCaretPosition(nativeElement, this.iframe);
        let charPressed = event.key;
        if (!charPressed) {
            const charCode = event.which || event.keyCode;
            if (!event.shiftKey && charCode >= 65 && charCode <= 90) {
                charPressed = String.fromCharCode(charCode + 32);
            } else {
                // TODO (dmacfarlane) fix this for non-alpha keys
                // http://stackoverflow.com/questions/2220196/how-to-decode-character-pressed-from-jquerys-keydowns-event-handler?lq=1
                charPressed = String.fromCharCode(event.which || event.keyCode);
            }
        }
        if (event.keyCode === ENTER && event.wasClick && pos < this.startPos) {
            // put caret back in position prior to contenteditable menu click
            pos = this.startNode.length;
            setCaretPosition(this.startNode, pos, this.iframe);
        }
        const config = this.triggerChars[charPressed];
        if (config) {
            this.activeConfig = config;
            this.startPos = event.inputEvent ? pos - 1 : pos;
            this.startNode = (
                this.iframe ? this.iframe.contentWindow.getSelection() : window.getSelection()
            ).anchorNode;
            this.searching = true;
            this.searchString = null;
            this.showSearchList(nativeElement);
            this.updateSearchList();
            if (config.returnTrigger) {
                this.searchTerm.emit(config.triggerChar);
            }
        } else if (/*this.startPos >= 0 &&*/ this.searching) {
            /*if (pos <= this.startPos) {
                this.searchList.hidden = true;
            }
            // ignore shift when pressed alone, but not when used with another key
            else*/ if (event.keyCode !== SHIFT && !event.metaKey && !event.altKey && !event.ctrlKey) {
                if (!this.activeConfig.allowSpace && event.keyCode === SPACE) {
                    this.startPos = -1;
                } else if (event.keyCode === BACKSPACE && pos > 0) {
                    pos--;
                    if (pos === this.startPos) {
                        this.stopSearch();
                    }
                } else if (this.searchList.hidden) {
                    if (event.keyCode === TAB || event.keyCode === ENTER) {
                        this.stopSearch();
                        return;
                    }
                } else if (!this.searchList.hidden) {
                    if (event.keyCode === TAB || event.keyCode === ENTER) {
                        this.stopEvent(event);

                        // optional function to format the selected item before inserting the text
                        const text = this.activeConfig.mentionSelect(
                            this.searchList.activeItem,
                            this.activeConfig.triggerChar
                        );

                        const formattedText = '<span class="chip" contenteditable="false">' + text + '</span>&#8203;';
                        const innerdiv = document.querySelectorAll('[contenteditable="true"]')[0] as HTMLDivElement;

                        this.pasteHtmlAtCaret(formattedText, innerdiv);
                        // fire input event so angular bindings are updated
                        if ('createEvent' in document) {
                            const evt = document.createEvent('HTMLEvents');
                            if (this.iframe) {
                                // a 'change' event is required to trigger tinymce updates
                                evt.initEvent('change', true, false);
                            } else {
                                evt.initEvent('input', true, false);
                            }
                            // this seems backwards, but fire the event from this elements nativeElement (not the
                            // one provided that may be in an iframe, as it won't be propagated)
                            this._element.nativeElement.dispatchEvent(evt);
                        }
                        this.startPos = -1;
                        this.stopSearch();
                        return false;
                    } else if (event.keyCode === ESCAPE) {
                        this.stopEvent(event);
                        this.stopSearch();
                        return false;
                    } else if (event.keyCode === DOWN_ARROW) {
                        this.stopEvent(event);
                        this.searchList.activateNextItem();
                        return false;
                    } else if (event.keyCode === UP_ARROW) {
                        this.stopEvent(event);
                        this.searchList.activatePreviousItem();
                        return false;
                    }
                }
                if (this.searching) {
                    let mention = val.substring(this.startPos + 1, pos);
                    if (event.keyCode !== BACKSPACE && !event.inputEvent) {
                        mention += charPressed;
                    }
                    this.searchString = mention;
                    if (this.activeConfig.returnTrigger) {
                        const triggerChar =
                            this.searchString || event.keyCode === BACKSPACE
                                ? val.substring(this.startPos, this.startPos + 1)
                                : '';
                        this.searchTerm.emit(triggerChar + this.searchString);
                    } else {
                        this.searchTerm.emit(this.searchString);
                    }
                    this.updateSearchList();
                }
            }
        }
    }

    mouseDownHandler(event, nativeElement = this._element.nativeElement) {
        const config = this.triggerChars['@'];
        if (config) {
            this.activeConfig = config;
            this.searching = true;
            this.searchString = null;
            this.showSearchList(nativeElement);
            this.updateSearchList();
            if (config.returnTrigger) {
                this.searchTerm.emit(config.triggerChar);
            }
        }
    }

    // exposed for external calls to open the mention list, e.g. by clicking a button
    startSearch(triggerChar, nativeElement = this._element.nativeElement) {
        triggerChar = triggerChar || this.mentionConfig.triggerChar || this.DEFAULT_CONFIG.triggerChar;
        const pos = getCaretPosition(nativeElement, this.iframe);
        insertValue(nativeElement, pos, pos, triggerChar, this.iframe);
        this.keyHandler({ key: triggerChar, inputEvent: true }, nativeElement);
    }

    stopSearch() {
        if (this.searchList && !this.searchList.hidden) {
            this.searchList.hidden = true;
            this.closed.emit();
        }
        this.activeConfig = null;
        this.searching = false;
    }

    updateSearchList() {
        let matches = [];
        if (this.activeConfig && this.activeConfig.items) {
            let objects = this.activeConfig.items;
            // disabling the search relies on the async operation to do the filtering
            if (!this.activeConfig.disableSearch && this.searchString && this.activeConfig.labelKey) {
                if (this.activeConfig.mentionFilter) {
                    objects = this.activeConfig.mentionFilter(this.searchString, objects);
                }
            }
            matches = objects;
            if (this.activeConfig.maxItems > 0) {
                matches = matches.slice(0, this.activeConfig.maxItems);
            }
        }
        // update the search list
        if (this.searchList) {
            this.searchList.items = this.mentionItems;
            this.searchList.hidden = this.mentionItems.length === 0;
        }
    }

    showSearchList(nativeElement) {
        this.opened.emit();
        if (!this.searchList) {
            const componentFactory = this._componentResolver.resolveComponentFactory(MentionListComponent);
            const componentRef = this._viewContainerRef.createComponent(componentFactory);
            this.searchList = componentRef.instance;
            this.searchList.itemTemplate = this.mentionListTemplate;
            componentRef.instance['itemClick'].pipe(untilDestroyed(this)).subscribe(() => {
                nativeElement.focus();
                const fakeKeydown = { key: 'Enter', keyCode: ENTER, wasClick: true };
                this.keyHandler(fakeKeydown, nativeElement);
            });
        }
        if (this.activeConfig === undefined) {
            this.updateConfig();
        }
        this.searchList.labelKey = this.activeConfig.labelKey;
        this.searchList.dropUp = this.activeConfig.dropUp;
        this.searchList.styleOff = this.mentionConfig.disableStyle;
        this.searchList.activeIndex = 0;
        this.searchList.position(nativeElement, this.iframe);
        window.requestAnimationFrame(() => this.searchList.reset());
    }

    pasteHtmlAtCaret(html, element: HTMLDivElement) {
        let sel: Selection, range: Range;
        if (window.getSelection) {
            sel = window.getSelection();
            if (this.elementContainsSelection(element)) {
                if (sel.getRangeAt && sel.rangeCount) {
                    range = sel.getRangeAt(0);
                    range.deleteContents();

                    const el = document.createElement('div');
                    el.innerHTML = html;
                    const frag = document.createDocumentFragment();
                    let node, lastNode;
                    const target = document.createTextNode('\u0001');
                    frag.appendChild(target);
                    while ((node = el.firstChild)) {
                        lastNode = frag.appendChild(node);
                    }
                    range.insertNode(frag);

                    let currentText = element.innerHTML;

                    const currentCaretPosition = currentText.indexOf('\u0001');
                    let startIndex = currentCaretPosition;

                    while (startIndex > 0) {
                        if (currentText[startIndex] === '@') {
                            break;
                        }
                        startIndex--;
                    }

                    let searchTextToDelete = currentText.substring(startIndex, currentCaretPosition + 1);
                    //Delete text that was entered when searching
                    if (searchTextToDelete.length > 0) {
                        element.innerHTML = element.innerHTML.replace(searchTextToDelete, '');
                        currentText = element.innerHTML;
                    }
                    startIndex = startIndex + html.length - 6;
                    let endIndex = startIndex;

                    while (endIndex < currentText.length) {
                        if (currentText[endIndex] === ' ' || currentText[endIndex] === '\n') {
                            break;
                        }
                        endIndex++;
                    }

                    searchTextToDelete = currentText.substring(startIndex, endIndex);
                    //Delete text that was entered when searching
                    if (searchTextToDelete.length > 0) {
                        element.innerHTML = element.innerHTML.slice(0, startIndex) + element.innerHTML.slice(endIndex);
                    }

                    const text = this.activeConfig.mentionSelect(
                        this.searchList.activeItem,
                        this.activeConfig.triggerChar
                    );
                    const children = element.children;
                    //Set caret position after the recently inserted chip
                    Array.from(children).forEach((child) => {
                        if (child.innerHTML === text) {
                            range = range.cloneRange();
                            range.setStartAfter(child);
                            range.collapse(false);
                            sel.removeAllRanges();
                            sel.addRange(range);
                        }
                    });

                    this.itemSelected.emit({
                        selectedItem: this.searchList.activeItem
                    });
                }
            } else {
                this.setEndOfContenteditable(element);
                this.pasteHtmlAtCaret(html, element);
            }
        }
    }

    elementContainsSelection(el) {
        let sel;
        if (window.getSelection) {
            sel = window.getSelection();
            if (sel.rangeCount > 0) {
                for (let i = 0; i < sel.rangeCount; ++i) {
                    if (!this.isOrContains(sel.getRangeAt(i).commonAncestorContainer, el)) {
                        return false;
                    }
                }
                return true;
            }
        } else if ((sel = document.getSelection()) && sel.type !== 'Control') {
            return this.isOrContains(sel.createRange().parentElement(), el);
        }
        return false;
    }

    isOrContains(node, container) {
        while (node) {
            if (node === container) {
                return true;
            }
            node = node.parentNode;
        }
        return false;
    }

    setEndOfContenteditable(contentEditableElement) {
        let range, selection;
        if (document.createRange) {
            range = document.createRange();
            range.selectNodeContents(contentEditableElement);
            range.collapse(false);
            selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(range);
        }
    }
}
